import { Injectable } from '@angular/core';
import { Store } from '@ngxs/store';
import { AuthState } from 'app/core/auth/core/auth.state';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { SystemRoles } from '~app/shared/security/system-roles';
import {
  enabledSecureMessageCategories,
  messageMatchesCategory,
  SECURE_MESSAGE_GROUPS
} from '../secure-message-categories';
import { PortalMessage } from '../store/portal-messages.model';
import { PortalMessagesService } from './portal-messages.service';

/** Feeds the sidebar's unread counters, independently of the lazy-loaded messages module. */
@Injectable({ providedIn: 'root' })
export class SecureMessagesNavService {
  private readonly messages = new BehaviorSubject<PortalMessage[]>([]);

  /** Unread message count by category key. */
  readonly unreadByCategory$: Observable<Record<string, number>> = this.messages.pipe(
    map(messages => {
      const unread = messages.filter(m => !m.isRead);
      const counts: Record<string, number> = {};
      SECURE_MESSAGE_GROUPS.forEach(g =>
        g.categories.forEach(c => (counts[c.key] = unread.filter(m => messageMatchesCategory(m, c)).length))
      );
      return counts;
    })
  );

  /** Members only see the enabled categories; non-members keep the full legacy inbox. */
  readonly totalUnread$: Observable<number> = this.messages.pipe(
    map(messages => {
      const unread = messages.filter(m => !m.isRead);
      if (!this.isMember()) {
        return unread.length;
      }
      const categories = enabledSecureMessageCategories();
      return unread.filter(m => categories.some(c => messageMatchesCategory(m, c))).length;
    })
  );

  constructor(private api: PortalMessagesService, private store: Store) {}

  refresh(): void {
    this.api
      .getMyMessages()
      .pipe(catchError(() => of<PortalMessage[]>([])))
      .subscribe(messages => this.messages.next(messages));
  }

  setMessages(messages: PortalMessage[]): void {
    this.messages.next(messages ?? []);
  }

  isMember(): boolean {
    const user = this.store.selectSnapshot(AuthState.user);
    return !!user?.roles?.includes(SystemRoles.roleMember);
  }
}
